import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import Button from '../Button/Button';
import './Header.css';

const Header = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user, isAuthenticated, logout, loading, login, token } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const hasFetchedBalance = useRef(false);

  useEffect(() => {
    if (isAuthenticated() && !hasFetchedBalance.current) {
      hasFetchedBalance.current = true;
      api.get('/v1/users', true)
        .then((response) => {
          const userData = response.data?.user || response.data;
          setBalance(userData?.coins != null ? { coins: userData.coins, frozenCoins: userData.frozenCoins } : null);
          // Update user context with fresh data (avatar, name, etc.)
          if (userData && token) {
            login(userData, token);
          }
        })
        .catch((err) => console.error('Failed to fetch balance:', err));
    }
    if (!isAuthenticated()) {
      setBalance(null);
      hasFetchedBalance.current = false;
    }
  }, [isAuthenticated()]);

  const handleLogout = () => {
    logout();
    setProfileMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const getUserInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.firstName) {
      return `${user.firstName[0]}${user.lastName?.[0] || ''}`.toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <header className="header">
      <div className="header-container">
        <div className="header-content">
          {/* Logo */}
          <Link to="/" className="header-logo">
            <img src="/logo.jpg" alt="OpenSMS" className="header-logo-img" />
            <h2 className="header-logo-text">OpenSMS</h2>
          </Link>

          {/* Desktop Navigation */}
          <nav className="header-nav">
            <Link to="/" className="header-nav-link">Home</Link>
            <Link to="/services" className="header-nav-link">Get Number</Link>
            <Link to="/faq" className="header-nav-link">FAQ</Link>
            <Link to="/contact" className="header-nav-link">Contact</Link>
          </nav>

          {/* Desktop Actions */}
          <div className="header-actions">
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              <span className="material-symbols-outlined">
                {isDark ? 'light_mode' : 'dark_mode'}
              </span>
            </button>

            {isAuthenticated() && balance ? (
              <Link to="/checkout" className="header-balance">
                <span className="material-symbols-outlined header-balance-icon">toll</span>
                <span className="header-balance-coins">{balance.coins?.toLocaleString() ?? 0}</span>
                <span className="header-balance-frozen" title="Frozen coins">
                  <span className="material-symbols-outlined header-frozen-icon">ac_unit</span>
                  {balance.frozenCoins?.toLocaleString() ?? 0}
                </span>
              </Link>
            ) : null}

            {isAuthenticated() ? (
              <div className="profile-dropdown">
                <button
                  className="profile-btn"
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                >
                  {user?.profilePicture || user?.avatar || user?.avatarUrl ? (
                    <img
                      src={user.profilePicture || user.avatar || user.avatarUrl}
                      alt="Profile"
                      className="profile-avatar"
                    />
                  ) : (
                    <div className="profile-avatar-placeholder">
                      {getUserInitials()}
                    </div>
                  )}
                  {(user?.firstName || user?.name) && (
                    <span className="profile-name">
                      {user.firstName || user.name?.split(' ')[0]}
                    </span>
                  )}
                  <span className="material-symbols-outlined dropdown-arrow">
                    {profileMenuOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>

                {profileMenuOpen && (
                  <div className="profile-menu">
                    <div className="profile-menu-header">
                      <p className="profile-menu-name">
                        {user?.name || user?.firstName || 'User'}
                      </p>
                      <p className="profile-menu-email">{user?.email}</p>
                    </div>
                    <div className="profile-menu-divider"></div>
                    <Link
                      to="/profile"
                      className="profile-menu-item"
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      <span className="material-symbols-outlined">person</span>
                      My Profile
                    </Link>
                    <Link
                      to="/history"
                      className="profile-menu-item"
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      <span className="material-symbols-outlined">phone_in_talk</span>
                      My Numbers
                    </Link>
                    <Link
                      to="/services"
                      className="profile-menu-item"
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      <span className="material-symbols-outlined">sms</span>
                      Get Number
                    </Link>
                    <Link
                      to="/history?tab=payments"
                      className="profile-menu-item"
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      <span className="material-symbols-outlined">receipt_long</span>
                      Payment History
                    </Link>
                    <div className="profile-menu-divider"></div>
                    <button className="profile-menu-item logout" onClick={handleLogout}>
                      <span className="material-symbols-outlined">logout</span>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/auth">
                  <Button variant="secondary" size="small">Sign In</Button>
                </Link>
                <Link to="/auth">
                  <Button variant="primary" size="small">Sign Up</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="material-symbols-outlined">
              {mobileMenuOpen ? 'close' : 'menu'}
            </span>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <Link to="/" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
              Home
            </Link>
            <Link to="/services" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
              Services
            </Link>
            <Link to="/long-term" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
              Long Term
            </Link>
            <Link to="/faq" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
              FAQ
            </Link>
            <Link to="/contact" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
              Contact
            </Link>

            {isAuthenticated() ? (
              <>
                <div className="mobile-menu-divider"></div>
                <div className="mobile-user-info">
                  {user?.profilePicture || user?.avatar || user?.avatarUrl ? (
                    <img
                      src={user.profilePicture || user.avatar || user.avatarUrl}
                      alt="Profile"
                      className="mobile-user-avatar"
                    />
                  ) : (
                    <div className="mobile-user-avatar-placeholder">
                      {getUserInitials()}
                    </div>
                  )}
                  <div className="mobile-user-details">
                    <p className="mobile-user-name">
                      {user?.name || user?.firstName || 'User'}
                    </p>
                    <p className="mobile-user-email">{user?.email}</p>
                  </div>
                </div>
                {balance && (
                  <Link to="/checkout" className="mobile-balance" onClick={() => setMobileMenuOpen(false)}>
                    <span className="material-symbols-outlined">toll</span>
                    <span className="mobile-balance-coins">{balance.coins?.toLocaleString() ?? 0} coins</span>
                    {balance.frozenCoins > 0 && (
                      <span className="mobile-balance-frozen">
                        <span className="material-symbols-outlined">ac_unit</span>
                        {balance.frozenCoins?.toLocaleString()} frozen
                      </span>
                    )}
                  </Link>
                )}
                <Link to="/profile" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
                  <span className="material-symbols-outlined">person</span>
                  My Profile
                </Link>
                <Link to="/history" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
                  <span className="material-symbols-outlined">phone_in_talk</span>
                  My Numbers
                </Link>
                <button className="mobile-menu-link logout-link" onClick={handleLogout}>
                  <span className="material-symbols-outlined">logout</span>
                  Sign Out
                </button>
              </>
            ) : (
              <div className="mobile-menu-actions">
                <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="secondary" fullWidth>Sign In</Button>
                </Link>
                <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="primary" fullWidth>Sign Up</Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
